/**
 * Property Scope Dropdown — searchable list of org properties with status dots.
 * Used inside ScopeBreadcrumb when in property scope.
 * On select: sets property scope + navigates to property dashboard.
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandItem,
} from '@/components/ui/command';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Per-property reachability for status dots. Lightweight query on plants table. */
type PropertyStatus = 'online' | 'warning' | 'offline' | 'unknown';

interface PropertyWithStatus {
  id: string;
  name: string;
  status: PropertyStatus;
}

function usePropertyStatuses(): Map<string, PropertyStatus> {
  const { properties, userProfile, overrideOrgId } = useApp();
  const orgId = overrideOrgId ?? userProfile?.org_id ?? null;
  const propertyIds = useMemo(() => properties.map((p) => p.id), [properties]);

  const { data } = useQuery({
    queryKey: ['property-statuses-for-dropdown', orgId, propertyIds.join(',')],
    queryFn: async () => {
      if (!propertyIds.length) return new Map<string, PropertyStatus>();
      // Query fleet_dashboard_cache for online/data_stale flags per property
      const { data: rows } = await supabase
        .from('fleet_dashboard_cache')
        .select('property_id, online, data_stale')
        .in('property_id', propertyIds);

      const statuses = new Map<string, PropertyStatus>();
      for (const row of rows ?? []) {
        if (!row.online) {
          statuses.set(row.property_id, 'offline');
        } else if (row.data_stale) {
          statuses.set(row.property_id, 'warning');
        } else {
          statuses.set(row.property_id, 'online');
        }
      }
      return statuses;
    },
    enabled: propertyIds.length > 0,
    staleTime: 60_000,
  });

  return data ?? new Map();
}

const STATUS_DOT: Record<PropertyStatus, string> = {
  online: 'bg-status-online',
  warning: 'bg-wt-amber-primary',
  offline: 'bg-status-offline',
  unknown: 'bg-muted-foreground/50',
};

interface PropertyScopeDropdownProps {
  currentPropertyId?: string;
  currentPropertyName?: string;
}

export function PropertyScopeDropdown({ currentPropertyId, currentPropertyName }: PropertyScopeDropdownProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { properties, setPropertyScope } = useApp();
  const statuses = usePropertyStatuses();

  const items: PropertyWithStatus[] = useMemo(
    () =>
      properties.map((p) => ({
        id: p.id,
        name: p.name,
        status: statuses.get(p.id) ?? 'unknown',
      })),
    [properties, statuses]
  );

  const showSearch = items.length >= 10;

  const handleSelect = (property: PropertyWithStatus) => {
    setPropertyScope(property.id, property.name);
    navigate(`/app/property/${property.id}`);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'inline-flex items-center gap-1 text-sm font-medium text-sidebar-foreground truncate max-w-[160px]',
            'hover:text-foreground transition-colors rounded px-1 -mx-1',
            open && 'text-foreground'
          )}
          title={currentPropertyName}
        >
          <span className="truncate">{currentPropertyName ?? 'Property'}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[260px] p-0 bg-wt-bg-primary border-border"
        side="bottom"
        align="start"
        sideOffset={4}
      >
        <Command className="bg-transparent">
          {showSearch && (
            <div className="flex items-center border-b border-border px-2">
              <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <CommandInput
                placeholder="Search properties..."
                className="h-9 text-sm bg-transparent border-0 focus:ring-0"
              />
            </div>
          )}
          <CommandList className="max-h-[280px]">
            <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">
              No properties found.
            </CommandEmpty>
            {items.map((item) => (
              <CommandItem
                key={item.id}
                value={item.name}
                onSelect={() => handleSelect(item)}
                className={cn(
                  'flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer',
                  item.id === currentPropertyId && 'bg-wt-bg-secondary'
                )}
              >
                <span
                  className={cn('h-2 w-2 rounded-full shrink-0', STATUS_DOT[item.status])}
                  aria-hidden
                />
                <span className="truncate text-foreground">{item.name}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
